import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Badge, EmptyState, LoadingState, ErrorState, Input } from "@/components/design-system";
import { CATEGORIES, CATEGORY_LABEL, listPublicProducts } from "@/lib/products";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");

export default function CommunityExplore() {
  const [state, setState] = useState({ status: "loading", items: [], total: 0, err: "" });
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const data = await listPublicProducts({ q, category, page, pageSize });
      setState({ status: "ready", items: data.items, total: data.total, err: "" });
    } catch (err) { setState({ status: "error", items: [], total: 0, err: formatApiError(err) }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category, page]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(state.total / pageSize));

  return (
    <div data-testid="community-explore-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Explore</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Discover SaaS products worth your feedback.</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Every product below is a real founder looking for qualified feedback. Structured campaigns open in Cycle C3.</p>

      <div className="mt-8 flex flex-col md:flex-row gap-3">
        <Input data-testid="explore-search" placeholder="Search by name or tagline…" value={q} onChange={(e) => setQ(e.target.value)} className="md:max-w-md" />
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => { setCategory(""); setPage(1); }} className={cn("px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap border", category === "" ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white text-[#0B1F3A]/70 border-[#0B1F3A]/15 hover:border-[#0B1F3A]/40")}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => { setCategory(c); setPage(1); }} className={cn("px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap border", category === c ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white text-[#0B1F3A]/70 border-[#0B1F3A]/15 hover:border-[#0B1F3A]/40")}>{CATEGORY_LABEL[c]}</button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        {state.status === "loading" && <LoadingState label="Loading products…" />}
        {state.status === "error" && <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState title="No products match your filters" description={q || category ? "Try clearing the filter or your search." : "Nothing published yet — check back soon."} />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="explore-grid">
            {state.items.map((p) => (
              <Card key={p.id} data-testid={`explore-card-${p.slug}`} className="!p-6 flex flex-col">
                <div className="flex items-start gap-4">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#0B1F3A]/10 bg-white" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-[#E8F1FF] border border-[#0B1F3A]/10 flex items-center justify-center text-[#2D7FF9] font-[Montserrat] font-extrabold text-xl">
                      {(p.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <Badge variant="community">{CATEGORY_LABEL[p.category] || p.category}</Badge>
                    <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] mt-2 truncate">{p.name}</h3>
                  </div>
                </div>
                <p className="text-sm text-[#0B1F3A]/75 mt-3 line-clamp-3 flex-1">{p.tagline}</p>
                <div className="mt-4 pt-4 border-t border-[#0B1F3A]/10 flex items-center justify-between">
                  <span className="text-xs text-[#0B1F3A]/60">By {p.founder_name}</span>
                  <Link to={`/p/${p.slug}`}><Button variant="secondary" data-testid={`explore-open-${p.slug}`}>Open</Button></Link>
                </div>
              </Card>
            ))}
          </div>
        )}
        {totalPages > 1 && state.status === "ready" && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Previous</Button>
            <span className="text-sm text-[#0B1F3A]/70">Page {page} / {totalPages}</span>
            <Button variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</Button>
          </div>
        )}
      </div>
    </div>
  );
}
